import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Box, Button, Spinner, Text } from '@thirstycamel/ui'
import { ReportingSelectionStore } from '../pages/api/getReportingSelectionStores'

const Wrapper = styled(Box)`
  padding-top: 16px;
`

const Panel = styled(Box)`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`

const StoreList = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  column-count: 1;
  column-gap: 24px;

  @media (min-width: 48em) {
    column-count: 2;
  }

  @media (min-width: 62em) {
    column-count: 3;
  }

  @media (min-width: 80em) {
    column-count: 4;
  }
`

const StoreItem = styled.li`
  break-inside: avoid;
  padding: 4px 0;
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StoreName = styled.span`
  font-weight: 500;
`

const StoreMeta = styled.span`
  color: rgba(0, 0, 0, 0.55);
`

type ReportingIncludedStoresProps = {
  title: string
  stores: ReportingSelectionStore[]
  loading?: boolean
  error?: string | null
  expanded: boolean
  onToggle: () => void
}

const formatStoreLine = (store: ReportingSelectionStore) => {
  const meta: string[] = []
  if (store.region) {
    meta.push(store.region)
  }
  if (store.humpClubStoreCode) {
    meta.push(store.humpClubStoreCode)
  }
  return meta
}

const ReportingIncludedStores: React.FC<ReportingIncludedStoresProps> = ({
  title,
  stores,
  loading = false,
  error = null,
  expanded,
  onToggle,
}) => {
  const sortedStores = useMemo(
    () =>
      [...stores].sort((a, b) =>
        a.storeName.localeCompare(b.storeName, undefined, { sensitivity: 'base' }),
      ),
    [stores],
  )

  return (
    <Wrapper>
      <Panel>
        <FlexRow>
          <Text fontWeight="600" color="gray.700">
            {title}
          </Text>
          <Button variantColor="blue" variant="outline" size="sm" onClick={onToggle}>
            {expanded ? 'Hide' : 'Show'} stores ({stores.length})
          </Button>
        </FlexRow>
        {expanded && (
          <Box mt={3}>
            {loading && (
              <Box display="flex" alignItems="center" gridGap={2}>
                <Spinner size="sm" />
                <Text color="gray.600" fontSize="sm">
                  Loading included stores…
                </Text>
              </Box>
            )}
            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
            {!loading && !error && stores.length === 0 && (
              <Text color="gray.600" fontSize="sm">
                No stores in this selection.
              </Text>
            )}
            {!loading && !error && stores.length > 0 && (
              <StoreList>
                {sortedStores.map(store => {
                  const meta = formatStoreLine(store)
                  const line =
                    meta.length > 0 ? `${store.storeName} · ${meta.join(' · ')}` : store.storeName
                  return (
                    <StoreItem key={store.storeId} title={line}>
                      <StoreName>{store.storeName}</StoreName>
                      {meta.length > 0 && (
                        <StoreMeta>
                          {' · '}
                          {meta.join(' · ')}
                        </StoreMeta>
                      )}
                    </StoreItem>
                  )
                })}
              </StoreList>
            )}
          </Box>
        )}
      </Panel>
    </Wrapper>
  )
}

const FlexRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export default ReportingIncludedStores
